import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import {
    Box,
    Alert,
    Badge,
    Text,
    ActionIcon,
    Tooltip,
    Group,
    ThemeIcon,
    SegmentedControl,
} from '@mantine/core';
import { IconCopy, IconLayoutGrid, IconTable } from '@tabler/icons-react';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import { getEntityIconAndColor } from '../../utils/navigationIconUtils';
import { EntityType, getEntityTypeString } from '../../types/ScimUser';
import { Person } from '../../types/Person';
import { entityService, userService } from '../../services/entityServiceSingleton';
import { DynamicTableView, type TableColumn, UsernameDisplay, DisplayNameCell } from '../common';
import { GuidDisplay, isGuid } from '../common/GuidDisplay';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { GenericEntityUpsertDialog } from '../dialogs';
import { pushHashRoute, pushHashRouteSuppressSmoothScroll } from '../../utils/hashNavigationUtils';
import { replaceHashRoute } from '../../utils/hashNavigationUtils';
import { createPersonManagementTableAdapter } from '../common/tableAdapters';
import { UnifiedEntityScreen } from '../entities/UnifiedEntityScreen';
import { buildEntityScreenHash } from '../../utils/entityScreenRouteUtils';
import { debugLog } from '../../utils/debugLog';
import { ShadcnCardGrid } from '../entities/ShadcnCardGrid';
import { addRecentPerson } from '../dashboard/RecentPeopleStrip';

interface PersonManagementProps {
    selectedEntityType?: EntityType;
    selectedEntityId?: string | null;
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    /** When this changes (e.g. on-behalf-of toggled or UPN set/cleared), the table refreshes. */
    onBehalfOfKey?: string;
    /** Called when we have the entity loaded so App can set the page title without fetching. */
    onEntityDisplayNameForTitle?: (displayName: string | null) => void;
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}

/**
 * Efficient Person Management component using SCIM attribute selection
 * Reduces server load by only requesting essential table view attributes
 */
const PersonManagement: React.FC<PersonManagementProps> = ({
    selectedEntityType,
    selectedEntityId,
    selectedEntityViewMode,
    deepLinkEntityType,
    authResolved = false,
    onBehalfOfKey,
    onEntityDisplayNameForTitle,
    onUnsavedStateChange
}) => {
    const { isStandard } = useViewModeSafe();

    const [listViewMode, setListViewMode] = useState<'table' | 'cards'>(() => {
        try {
            const stored = localStorage.getItem('hades_person_list_view_mode');
            if (stored === 'cards' || stored === 'table') return stored;
        } catch { /* noop */ }
        return 'cards';
    });

    useEffect(() => {
        try { localStorage.setItem('hades_person_list_view_mode', listViewMode); } catch { /* noop */ }
    }, [listViewMode]);

    // UI state only (not data state)
    const [selectedPerson, setSelectedPerson] = useState<any>(null);
    const [editingPerson, setEditingPerson] = useState<any>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [cloneSourcePerson, setCloneSourcePerson] = useState<any>(null);
    const itemsPerPage = 25;

    const [error, setError] = useState<string | null>(null);
    const [refreshSignal, setRefreshSignal] = useState(0);
    const newUiOpen = !!selectedEntityId
        && !!selectedEntityType
        && (selectedEntityViewMode === 'view' || selectedEntityViewMode === 'edit');

    /** Captures the list-view hash (including query params) so closing view/edit returns to the exact list state. */
    const lastListHashRef = useRef<string | null>(null);
    /**
     * Captures the list context (All Users vs specific subtype list) at the moment we opened a detail/edit.
     * Needed because once the hash becomes #Person/{subtype}/view/{id}, App will set selectedEntityType=subtype,
     * but close should return to the originating list (often #Person for All Users).
     */
    const originSelectedEntityTypeRef = useRef<EntityType | null | undefined>(selectedEntityType);
    const editPencilClickedRef = useRef(false);

    // Seed stub entity for UnifiedEntityScreen on deep link / reload.
    useEffect(() => {
        if (!newUiOpen || !selectedEntityId || !selectedEntityType) return;
        const stub = {
            Id: selectedEntityId,
            EntityType: selectedEntityType,
        } as unknown as Person;
        if (selectedEntityViewMode === 'edit') {
            if (!editingPerson || (editingPerson.Id ?? editingPerson.id) !== selectedEntityId) {
                setEditingPerson(stub);
            }
        } else if (!selectedPerson || (selectedPerson.Id ?? selectedPerson.id) !== selectedEntityId) {
            setSelectedPerson(stub);
        }
    }, [newUiOpen, selectedEntityId, selectedEntityViewMode, selectedEntityType, editingPerson, selectedPerson]);

    // Bulk delete + row error state is handled inside DynamicTableView now.

    // Report entity display name to App for page title when we have the loaded entity (no extra fetch)
    useEffect(() => {
        if (!onEntityDisplayNameForTitle || !selectedEntityId) {
            if (onEntityDisplayNameForTitle) onEntityDisplayNameForTitle(null);
            return;
        }
        const name = (entity: any) => entity?.displayName ?? entity?.DisplayName ?? null;
        if (selectedEntityViewMode === 'view' && selectedPerson?.id === selectedEntityId) {
            onEntityDisplayNameForTitle(name(selectedPerson) ?? null);
        } else if (selectedEntityViewMode === 'edit' && editingPerson?.id === selectedEntityId) {
            onEntityDisplayNameForTitle(name(editingPerson) ?? null);
        } else {
            onEntityDisplayNameForTitle(null);
        }
    }, [selectedEntityId, selectedEntityViewMode, selectedPerson, editingPerson, onEntityDisplayNameForTitle]);

    // Derive entity type from hash when routing hasn't set selectedEntityType yet (avoids rogue "Person" load before #Person/shared is applied)
    const entityTypeFromHash = useMemo(() => {
        if (selectedEntityType != null) return null;
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        const parts = hash.replace(/^#/, '').split('?')[0].split('/');
        if (parts[0]?.toLowerCase() === 'person' && (parts.length === 2 || parts.length === 3)) {
            return (parts[1] || '').toLowerCase();
        }
        return null;
    }, [selectedEntityType]);

    const effectiveEntityType = selectedEntityType ?? entityTypeFromHash;
    const effectiveEntityTypeForHook = effectiveEntityType
        ? (typeof effectiveEntityType === 'string' && effectiveEntityType === effectiveEntityType.toLowerCase()
            ? effectiveEntityType
            : getEntityTypeString(effectiveEntityType as EntityType).toLowerCase())
        : 'person';

    const {
        allowedTableAttributeIds,
        allowedSearchAttributeIds,
        allowedTableAttributeIdsRef,
        canEditRow,
        isDisjointed
    } = usePerEntityTableEntitlement({
        // Tier2 for column/search gating and edit/bulk-delete UI. Do not pass onResolved: DynamicTableView
        // already loads once when its own entitlementResolved flips; reload here caused a duplicate list fetch.
        entityType: effectiveEntityType ? effectiveEntityTypeForHook : 'person',
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet: false
    });

    // Tier2 prefetch for All Users is handled by the hook (entityType='person').

    const handleRefresh = useCallback(() => {
        setRefreshSignal((n) => n + 1);
    }, []);

    const getCurrentHashSearch = useCallback(() => {
        const currentHash = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
        const idx = currentHash.indexOf('?');
        return idx >= 0 ? currentHash.slice(idx) : '';
    }, []);

    const buildNewUiHash = useCallback((mode: 'view' | 'edit', entityId: string, entityTypeValue?: EntityType | string | null) => {
        const typeValue = entityTypeValue ?? selectedEntityType;
        const entityTypeStr = typeof typeValue === 'string' ? typeValue : (typeValue ? getEntityTypeString(typeValue) : '');
        if (!entityTypeStr) return null;
        const baseHash = `#Person/${entityTypeStr.toLowerCase()}`;
        const next = buildEntityScreenHash(baseHash, mode, entityId);
        const currentSearch = getCurrentHashSearch();
        return currentSearch && !next.includes('?') ? `${next}${currentSearch}` : next;
    }, [getCurrentHashSearch, selectedEntityType]);

    const getPersonEntityTypeValue = useCallback((entityLike: { EntityType?: unknown } | null | undefined): string => {
        return getEntityTypeString(entityLike?.EntityType) || '';
    }, []);

    // Table data loading/search/paging/sorting is handled by DynamicTableView.

    // Memoize create dialog callbacks to prevent re-renders
    const handleCreateDialogClose = useCallback(() => setCreateDialogOpen(false), []);
    const handleCreateDialogSuccess = useCallback(() => {
        setCreateDialogOpen(false);
        handleRefresh();
    }, [handleRefresh]);

    const handlePersonSelect = async (person: Person) => {
        try {
            setSelectedPerson(person);

            const etStr = typeof person.EntityType === 'string'
                ? person.EntityType
                : (person.EntityType as any)?.DisplayName || selectedEntityType ? getEntityTypeString(selectedEntityType!) : 'Person';
            addRecentPerson({
                id: person.Id,
                displayName: person.DisplayName || '',
                entityType: etStr,
                email: person.Email || undefined,
                viewedAt: Date.now(),
            });

            // Update URL hash for deep linking capability
            // Only update URL hash if this wasn't triggered by deep linking
            // If selectedEntityId matches the person ID, this was a deep link selection
            const isDeepLinkSelection = selectedEntityId === person.Id;

            if (person.Id && person.EntityType && !isDeepLinkSelection) {
                const currentHash = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
                // Record list context so close returns to the same list (All Users vs subtype) even after hash becomes #Person/{type}/view/{id}.
                originSelectedEntityTypeRef.current = selectedEntityType;
                const idx0 = currentHash.indexOf('?');
                const currentSearch0 = idx0 >= 0 ? currentHash.slice(idx0) : '';
                const listBaseHash = selectedEntityType ? `#Person/${getEntityTypeString(selectedEntityType).toLowerCase()}` : '#Person';
                lastListHashRef.current = currentSearch0 && !listBaseHash.includes('?') ? `${listBaseHash}${currentSearch0}` : listBaseHash;
                const baseHash = buildNewUiHash('view', person.Id, getPersonEntityTypeValue(person));
                // pushState (+ hashchange) keeps list URL in history so Back returns from detail; suppress smooth scroll
                if (baseHash) {
                    pushHashRouteSuppressSmoothScroll(baseHash);
                }
            }
        } catch (err) {
            console.error('âŒ PersonManagement: Error fetching full user data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load user details');
        }
    };

    const handleEditPerson = async (person: Person) => {
        editPencilClickedRef.current = true;
        setSelectedPerson(null);
        const entityId = person?.Id;
        if (entityId) {
            const currentHash = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
            originSelectedEntityTypeRef.current = selectedEntityType;
            const idx0 = currentHash.indexOf('?');
            const currentSearch0 = idx0 >= 0 ? currentHash.slice(idx0) : '';
            const listBaseHash = selectedEntityType ? `#Person/${getEntityTypeString(selectedEntityType).toLowerCase()}` : '#Person';
            lastListHashRef.current = currentSearch0 && !listBaseHash.includes('?') ? `${listBaseHash}${currentSearch0}` : listBaseHash;
            const newHash = buildNewUiHash('edit', entityId, getPersonEntityTypeValue(person));
            if (newHash) {
                pushHashRoute(newHash);
            }
        }
        try {
            // Fetch the full entity data for editing
            let fullUser;
            if (person.EntityType) {
                // Use entity-specific endpoint for specific person types
                debugLog(`âœï¸ PersonManagement: Fetching ${person.EntityType} entity for editing, ID: ${person.Id}`);
                fullUser = await entityService.getEntityById(person.Id, person.EntityType.toLowerCase(), false); // Get full expansion for editing
            } else {
                // Fallback to Person endpoint for entities without specific type
                debugLog(`âœï¸ PersonManagement: No EntityType found, using Person endpoint for editing, ID: ${person.Id}`);
                fullUser = await userService.getUser(person.Id);
            }
            setEditingPerson(fullUser);
        } catch (err) {
            console.error('âŒ PersonManagement: Error fetching user data for editing:', err);
            setError(err instanceof Error ? err.message : 'Failed to load user for editing');
        } finally {
            editPencilClickedRef.current = false;
        }
    };

    const maxUsernameLength = 18;

    // Simple Id display component with copy functionality (for non-GUID IDs)
    const IdDisplayWithCopy: React.FC<{ value: string }> = ({ value }) => {
        const [copied, setCopied] = React.useState(false);

        const handleCopy = async (event: React.MouseEvent) => {
            event.stopPropagation(); // Prevent row click from opening detail view
            const success = await copyToClipboard(value);
            if (success) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        };

        return (
            <Group gap={4} align="center" wrap="nowrap">
                <Text
                    size="sm"
                    ff="monospace"
                    style={{ cursor: 'default', minWidth: 0, overflow: 'hidden' }}
                    title={value}
                >
                    {value}
                </Text>
                <Tooltip label={copied ? "Copied!" : "Copy ID"}>
                    <ActionIcon
                        size="xs"
                        variant="subtle"
                        onClick={handleCopy}
                        color={copied ? "green" : "gray"}
                    >
                        <IconCopy size={14} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        );
    };

    // Get entity type icon and color for avatar (centralized so nav and list match)
    const getEntityTypeIcon = useCallback((person: Person) => {
        let entityTypeStr = '';
        if (typeof person.EntityType === 'string') {
            entityTypeStr = person.EntityType;
        } else if (person.EntityType && typeof person.EntityType === 'object') {
            entityTypeStr = (person.EntityType as any).DisplayName || (person.EntityType as any).Id || '';
        }
        const entityType = entityTypeStr || getEntityTypeString(selectedEntityType) || 'Person';
        const { Icon, color } = getEntityIconAndColor(entityType);
        return { icon: <Icon />, color };
    }, [selectedEntityType]);

    const PERSON_SUBTYPE_BADGE_COLORS: Record<string, string> = {
        employee: 'blue',
        student: 'green',
        contractor: 'orange',
        applicant: 'yellow',
        public: 'gray',
        sponsored: 'violet',
        shared: 'teal',
        vacancy: 'red',
    };

    const resolveEntityTypeStr = (person: Person): string => {
        if (typeof person.EntityType === 'string') return person.EntityType;
        if (person.EntityType && typeof person.EntityType === 'object') {
            return (person.EntityType as any).DisplayName || (person.EntityType as any).Id || '';
        }
        return '';
    };

    const columns: TableColumn<Person>[] = [
        {
            key: 'DisplayName',
            label: 'Name',
            title: 'DisplayName',
            width: '200px',
            sortable: true,
            render: (person: Person, index: number) => {
                const { icon, color } = getEntityTypeIcon(person);
                const entityType = resolveEntityTypeStr(person) || getEntityTypeString(selectedEntityType) || 'Person';

                return (
                    <Group gap="xs" align="center" wrap="nowrap">
                        <ThemeIcon
                            size={32}
                            radius="xl"
                            color={color}
                            variant="filled"
                            title={`Entity Type: ${entityType}`}
                        >
                            {icon}
                        </ThemeIcon>
                        <DisplayNameCell
                            displayName={person.DisplayName || person.rawData?.DisplayName}
                            id={person.Id}
                            index={index}
                            startIndex={0}
                        />
                    </Group>
                );
            }
        },
        ...(selectedEntityType === 'Vacancy'
            ? []
            : [{ key: 'Email', label: 'Email', width: '200px', sortable: true }]),
        ...((selectedEntityType === 'Applicant' || selectedEntityType === 'Contractor')
            ? [{ key: 'JobCode', label: 'Job Code', width: '140px', sortable: true }]
            : []),
        ...((selectedEntityType === 'Employee' || selectedEntityType === 'Contractor' || selectedEntityType === 'Sponsored' || selectedEntityType === 'Vacancy')
            ? [{ key: 'Department', label: 'Department', width: '150px', sortable: true,
                render: (person: Person) => (person.rawData?.Department || person.rawData?.JobTitle || '') }]
            : []),
        ...(selectedEntityType === 'Student'
            ? [{ key: 'GradeLevel', label: 'Grade', width: '100px', sortable: true,
                render: (person: Person) => (person.rawData?.GradeLevel || '') }]
            : []),
        {
            key: 'UserPrincipalName',
            label: 'UserPrincipalName',
            width: '250px',
            sortable: true,
            render: (person: Person) => (
                <UsernameDisplay
                    username={person.UserPrincipalName || person.Username || person.rawData?.UserPrincipalName || '-'}
                    variant="table"
                    maxLength={Math.min(maxUsernameLength, 18)}
                />
            )
        },
        ...(selectedEntityType ? [] : [{
            key: 'EntityType',
            label: 'Type',
            width: '130px',
            sortable: true,
            render: (person: Person) => {
                const typeStr = resolveEntityTypeStr(person) || 'Unknown';
                const badgeColor = PERSON_SUBTYPE_BADGE_COLORS[typeStr.toLowerCase()] || 'gray';
                return <Badge variant="light" color={badgeColor} size="sm">{typeStr}</Badge>;
            }
        }]),
        {
            key: 'Id',
            label: 'ID',
            width: '80px',
            sortable: true,
            render: (person: Person) => {
                const id = person.Id || person.rawData?.Id || '-';
                if (!id || id === '-') return <Text size="sm">-</Text>;
                if (isGuid(id)) {
                    return <GuidDisplay value={id} variant="chip" showCopyButton={true} showExpandButton={false} />;
                }
                return <IdDisplayWithCopy value={id} />;
            }
        },
    ];

    // Filter column list by tier2: show only columns the user is entitled to (Id and DisplayName always allowed).
    const allowedSet = Array.isArray(allowedTableAttributeIds)
        ? new Set(allowedTableAttributeIds.map((id) => (id || '').toLowerCase()))
        : null;
    const alwaysShowKeys = new Set(['id', 'displayname']);
    const filteredColumns: TableColumn<Person>[] =
        allowedSet === null
            ? columns
            : columns.filter((col) => {
                const key = (col.key || col.title || '').toString().toLowerCase();
                if (alwaysShowKeys.has(key)) return true;
                if (allowedSet.size === 0) return false;
                return allowedSet.has(key);
            });

    const personTableAdapter = useMemo(
        () =>
            createPersonManagementTableAdapter({
                effectiveEntityTypeForHook,
                selectedEntityType,
                itemsPerPage,
                entityService,
                allowedTableAttributeIdsRef,
                getFilteredColumns: () => filteredColumns
            }),
        [
            effectiveEntityTypeForHook,
            selectedEntityType,
            itemsPerPage,
            entityService,
            allowedTableAttributeIdsRef,
            filteredColumns
        ]
    );

    const cardRenderer = useCallback((props: {
        data: any[];
        isLoading: boolean;
        currentPage: number;
        totalResults: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
        onRowClick?: (item: any) => void;
    }) => {
        const visibleFieldSet = allowedSet
            ? new Set([...allowedSet, 'id', 'displayname'])
            : undefined;
        const entityTypeStr = selectedEntityType ? getEntityTypeString(selectedEntityType) : 'Person';
        return (
            <ShadcnCardGrid
                data={props.data}
                entityType={entityTypeStr}
                isLoading={props.isLoading}
                currentPage={props.currentPage}
                totalResults={props.totalResults}
                itemsPerPage={props.itemsPerPage}
                onPageChange={props.onPageChange}
                onEntitySelect={(entity) => props.onRowClick?.(entity)}
                isPerson
                selectedEntityType={selectedEntityType ? getEntityTypeString(selectedEntityType) : undefined}
                visibleFields={visibleFieldSet}
            />
        );
    }, [allowedSet, selectedEntityType]);

    return (
        <Box>
            {error && (
                <Alert color="red" mb="md">
                    {error}
                </Alert>
            )}

            {!newUiOpen && (
                <DynamicTableView
                    entityType={personTableAdapter.entityType}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    hideHeader={false}
                    onBehalfOfKey={onBehalfOfKey}
                    loadData={personTableAdapter.loadData}
                    generateColumns={() => personTableAdapter.getColumns()}
                    refreshSignal={refreshSignal}
                    onEntitySelect={selectedEntityViewMode === 'edit' ? undefined : handlePersonSelect}
                    onEditRowClick={canEditRow ? handleEditPerson : undefined}
                    allowedSearchAttributeIds={allowedSearchAttributeIds !== null ? allowedSearchAttributeIds : undefined}
                    deleteEntityById={personTableAdapter.deleteById}
                    onCloseCreateDialog={() => {
                        const newHash = selectedEntityType ? `#Person/${getEntityTypeString(selectedEntityType).toLowerCase()}` : '#Person';
                        pushHashRouteSuppressSmoothScroll(newHash);
                    }}
                    customDataRenderer={listViewMode === 'cards' ? cardRenderer : undefined}
                    customHeaderContent={
                        <SegmentedControl
                            value={listViewMode}
                            onChange={(val) => setListViewMode(val as 'table' | 'cards')}
                            size="xs"
                            data={[
                                { label: <Group gap={4} wrap="nowrap"><IconTable size={14} /><Text size="xs">Table</Text></Group>, value: 'table' },
                                { label: <Group gap={4} wrap="nowrap"><IconLayoutGrid size={14} /><Text size="xs">Cards</Text></Group>, value: 'cards' },
                            ]}
                        />
                    }
                />
            )}

            <UnifiedEntityScreen
                open={newUiOpen}
                entityType={selectedEntityType ? getEntityTypeString(selectedEntityType) : 'Person'}
                entityId={selectedEntityId ?? ''}
                mode={selectedEntityViewMode === 'edit' ? 'edit' : 'view'}
                existingEntity={editingPerson ?? selectedPerson}
                onClose={() => {
                    const origin = originSelectedEntityTypeRef.current;
                    const fallbackHash = origin ? `#Person/${getEntityTypeString(origin).toLowerCase()}` : '#Person';
                    replaceHashRoute(lastListHashRef.current || fallbackHash);
                }}
                onModeChange={(mode) => {
                    if (!selectedEntityId || !selectedEntityType) return;
                    const next = buildNewUiHash(mode, selectedEntityId, selectedEntityType);
                    if (!next) return;
                    replaceHashRoute(next);
                }}
                onSuccess={() => {
                    const origin = originSelectedEntityTypeRef.current;
                    const fallbackHash = origin ? `#Person/${getEntityTypeString(origin).toLowerCase()}` : '#Person';
                    replaceHashRoute(lastListHashRef.current || fallbackHash);
                    handleRefresh();
                }}
                onUnsavedStateChange={onUnsavedStateChange}
            />

            {/* Create Dialog - uses generic table create button or clone from detail; entityType from current view or clone source */}
            {selectedEntityType && (
                <GenericEntityUpsertDialog
                    key={cloneSourcePerson ? `clone-person-${cloneSourcePerson.Id || cloneSourcePerson.id}` : 'create-person'}
                    open={createDialogOpen}
                    onClose={() => { handleCreateDialogClose(); setCloneSourcePerson(null); }}
                    onSuccess={() => { handleCreateDialogSuccess(); setCloneSourcePerson(null); }}
                    entityType={cloneSourcePerson ? (getPersonEntityTypeValue(cloneSourcePerson) || 'Person') : getEntityTypeString(selectedEntityType)}
                    existingEntity={cloneSourcePerson ? { ...cloneSourcePerson, Id: undefined, id: undefined, DisplayName: cloneSourcePerson.DisplayName ? `${cloneSourcePerson.DisplayName} (Copy)` : undefined } : undefined}
                />
            )}
        </Box>
    );
};

export default PersonManagement;
